(() => {
  'use strict';
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.root = this.closest('[data-product-section]');
      this.previewVariants = [];
      try { this.previewVariants = JSON.parse(this.querySelector('[data-draft-preview-variants]')?.textContent || '[]'); } catch {}
      this.controller = new AbortController();
      this.addEventListener('change', event => {
        if (event.target.matches('[data-option-value-id]')) this.update(event.target, event.detail?.focusTargetId);
      }, { signal: this.controller.signal });
      this.root?.addEventListener('click', event => {
        if (!event.target.closest('[data-configure]')) return;
        event.preventDefault();
        const motion = document.body.classList.contains('animations-enabled') && !matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView({ behavior: motion ? 'smooth' : 'instant', block: 'center' });
        this.querySelector('input:checked')?.focus({ preventScroll: true });
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.requestController?.abort();
      this.controller = null;
    }
    async update(input, focusTargetId) {
      this.requestController?.abort();
      this.requestController = new AbortController();
      const signal = this.requestController.signal;
      const owner = this.root;
      if (!owner?.isConnected) return;
      const previousUrl = owner.dataset.productUrl;
      const productUrl = new URL(input.dataset.productUrl || previousUrl, location.origin);
      if (productUrl.origin !== location.origin) return;
      const currentUrl = new URL(location.href);
      const sameProduct = productUrl.pathname === new URL(previousUrl, location.origin).pathname;
      const draftPreview = sameProduct && currentUrl.pathname === '/products_preview' && currentUrl.searchParams.has('preview_key');
      const url = draftPreview ? currentUrl : productUrl;
      const activeTheme = window.Shopify?.theme;
      const previewId = new URL(location.href).searchParams.get('preview_theme_id') || (activeTheme && ['unpublished', 'development'].includes(activeTheme.role) ? activeTheme.id : null);
      if (previewId) url.searchParams.set('preview_theme_id', String(previewId));
      const selected = [...this.querySelectorAll('[data-option-value-id]:checked')].map(value => value.dataset.optionValueId);
      const selectedValues = [...this.querySelectorAll('[data-option-value-id]:checked')].map(value => value.value);
      url.searchParams.delete('variant');
      url.searchParams.set('option_values', selected.join(','));
      const draftMatch = draftPreview && this.previewVariants.find(variant => Array.isArray(variant.options) && variant.options.length === selectedValues.length && variant.options.every((value, i) => value === selectedValues[i]));
      const resolvedId = draftMatch ? String(draftMatch.id) : this.previewVariants.length ? null : input.dataset.resolvedVariantId;
      const draftVariantId = draftPreview && /^\d+$/.test(resolvedId || '') ? resolvedId : null;
      if (draftVariantId) { url.searchParams.delete('option_values'); url.searchParams.set('variant', draftVariantId); }
      if (!sameProduct) { location.assign(url.pathname + url.search); return; }
      const sectionId = owner.dataset.sectionId;
      const dependents = [...document.querySelectorAll('[data-product-dependent][data-section-id]')];
      const ids = [...new Set([sectionId, ...dependents.map(node => node.dataset.sectionId)])];
      const groups = [];
      for (let index = 0; index < ids.length; index += 5) groups.push(ids.slice(index, index + 5));
      const focusId = focusTargetId || input.id;
      const focusValue = input.value;
      const position = input.dataset.optionPosition;
      const quantity = Number(owner.querySelector('[data-product-quantity]')?.value || 1);
      const buttons = [...owner.querySelectorAll('button[name="add"]')];
      buttons.forEach(button => { button.disabled = true; });
      owner.setAttribute('aria-busy', 'true');
      const errorRegion = this.querySelector('[data-variant-error]');
      if (errorRegion) errorRegion.hidden = true;
      try {
        if (draftPreview && this.previewVariants.length && !draftVariantId) throw new Error('Draft preview combination is unavailable');
        const payloads = await Promise.all(groups.map(async group => {
          const endpoint = new URL(url);
          endpoint.searchParams.set('sections', group.join(','));
          const response = await fetch(endpoint, { signal, headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' } });
          if (!response.ok) throw new Error('Product sections could not be loaded');
          return response.json();
        }));
        if (signal.aborted || !owner.isConnected) return;
        const sections = Object.assign({}, ...payloads);
        const parse = (id, selector) => {
          if (!sections[id]) throw new Error('Product section missing');
          const parsed = new DOMParser().parseFromString(sections[id], 'text/html');
          parsed.querySelectorAll('noscript').forEach(fallback => fallback.remove());
          const node = parsed.querySelector(selector);
          if (!node) throw new Error('Product content missing');
          return node;
        };
        const next = parse(sectionId, '[data-product-section]');
        if (draftVariantId && next.dataset.variantId !== draftVariantId) throw new Error('Draft preview variant did not update');
        const replacements = dependents.map(node => ({node, next:parse(node.dataset.sectionId, '[data-product-dependent]')}));
        replacements.forEach(({node, next: fragment}) => {
          const activeIds = new Set([...node.querySelectorAll('[role="tab"][aria-selected="true"]')].map(tab => tab.id));
          const panels = [...fragment.querySelectorAll('[role="tabpanel"]')];
          fragment.querySelectorAll('product-tabs').forEach(container => {
            const tabs = [...container.querySelectorAll('[role="tab"]')].filter(tab => tab.closest('product-tabs') === container);
            const selectedTab = tabs.find(tab => activeIds.has(tab.id));
            if (!selectedTab) return;
            tabs.forEach(tab => {
              const active = tab === selectedTab;
              tab.setAttribute('aria-selected', String(active));
              tab.tabIndex = active ? 0 : -1;
              const panel = panels.find(candidate => candidate.id === tab.getAttribute('aria-controls'));
              if (panel) panel.hidden = !active;
            });
          });
        });
        const nextQuantity = next.querySelector('[data-product-quantity]');
        if (nextQuantity) {
          const min = Number(nextQuantity.min || 1), step = Number(nextQuantity.step || 1), max = Number(nextQuantity.max || Infinity);
          const safeQuantity = Number.isFinite(quantity) ? quantity : min;
          nextQuantity.value = String(min + Math.max(0, Math.floor((Math.min(max, safeQuantity) - min) / step)) * step);
          const stickyQuantity = next.querySelector('[data-sticky-quantity]');
          if (stickyQuantity) stickyQuantity.value = nextQuantity.value;
        }
        const variantId = next.dataset.variantId;
        if (variantId) { url.searchParams.delete('option_values'); url.searchParams.set('variant', variantId); }
        document.dispatchEvent(new CustomEvent('olea:product-sections-before-replace', {detail:{productId:owner.dataset.productId,nextProductId:next.dataset.productId}}));
        owner.replaceWith(next);
        replacements.forEach(replacement => replacement.node.replaceWith(replacement.next));
        window.OleaProductReveal?.(next);
        window.OleaMotion?.init(next);
        replacements.forEach(replacement => window.OleaProductReveal?.(replacement.next));
        replacements.forEach(replacement => window.OleaMotion?.init(replacement.next));
        history.replaceState({}, '', url.pathname + url.search + location.hash);
        const nextFocus = document.getElementById(focusId) || [...next.querySelectorAll('[data-option-position]')].find(node => node.dataset.optionPosition === position && node.value === focusValue);
        nextFocus?.focus({ preventScroll: true });
        document.dispatchEvent(new CustomEvent('olea:variant-change', {detail:{variantId,productId:next.dataset.productId,productUrl:next.dataset.productUrl,url:url.pathname+url.search,productChanged:previousUrl!==next.dataset.productUrl}}));
      } catch (error) {
        if (error.name === 'AbortError' || signal.aborted || !owner.isConnected) return;
        owner.removeAttribute('aria-busy');
        if (errorRegion) {
          errorRegion.hidden = false;
          const retry = errorRegion.querySelector('[data-variant-retry]');
          if (retry) retry.href = url.pathname + url.search;
        }
        buttons.forEach(button => { button.disabled = true; });
      }
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);
})();
